import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { AnalysisVersionResponse } from "@/api/types";
import {
  isLatestVersion,
  versionDateLabel,
  versionModelLabel,
} from "./helpers";

export interface VersionsTabProps {
  versions: AnalysisVersionResponse[];
}

/**
 * Versions tab — operator-facing history of analysis runs for this meeting.
 *
 * TaskMe aa7e38eb: show model + date + «(актуальная)» badge for the last row
 * so versions are visually distinguishable (model column matters because
 * rerun(N=1, claude) and rerun(N=1, openai) used to look identical).
 *
 * TaskMe ab1babf1: each row carries a collapsible «Промпт» disclosure that
 * shows the exact prompt text used by that analysis run. Adjacent rows that
 * point at the same `prompt_version_id` collapse into a back-reference so
 * operators don't scroll through 5×10 KB of identical text.
 */
export function VersionsTab({ versions }: VersionsTabProps) {
  const { t } = useTranslation();
  // Pre-compute back-references in display order. The contract says: walk
  // versions[] sequentially, and if the current row shares prompt_version_id
  // with the previous row, point back to the previous row's `version`.
  const sameAsByIndex = useMemo(() => computeSameAs(versions), [versions]);
  if (versions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("meetings.card.versionsEmpty")}
      </p>
    );
  }
  return (
    <div className="space-y-3" data-testid="versions-tab">
      <table className="w-full text-left text-sm">
        <thead className="text-muted-foreground">
          <tr>
            <th className="py-1 pr-4 font-medium">
              {t("meetings.card.version.columns.version")}
            </th>
            <th className="py-1 pr-4 font-medium">
              {t("meetings.card.version.columns.model")}
            </th>
            <th className="py-1 pr-4 font-medium">
              {t("meetings.card.version.columns.date")}
            </th>
            <th className="py-1 pr-4 font-medium">
              {t("meetings.card.version.columns.status")}
            </th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v, idx) => (
            <VersionRow
              key={v.id}
              version={v}
              latest={isLatestVersion(v, versions)}
              sameAsVersion={sameAsByIndex[idx] ?? null}
              t={t}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Map `i -> previous version number with the same prompt_version_id` (or null). */
function computeSameAs(versions: AnalysisVersionResponse[]): (number | null)[] {
  const out: (number | null)[] = [];
  for (let i = 0; i < versions.length; i += 1) {
    if (i === 0) {
      out.push(null);
      continue;
    }
    const cur = versions[i]!;
    const prev = versions[i - 1]!;
    out.push(
      cur.prompt_version_id === prev.prompt_version_id ? prev.version : null,
    );
  }
  return out;
}

interface VersionRowProps {
  version: AnalysisVersionResponse;
  latest: boolean;
  sameAsVersion: number | null;
  t: TFunction;
}

function VersionRow({ version, latest, sameAsVersion, t }: VersionRowProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr data-testid="versions-tab-row">
        <td className="py-1 pr-4 font-mono">#{version.version}</td>
        <td className="py-1 pr-4">{versionModelLabel(version, t)}</td>
        <td className="py-1 pr-4">{versionDateLabel(version)}</td>
        <td className="py-1 pr-4">
          {t(`meetings.card.status.${version.status}`)}
          {latest ? (
            <span
              data-testid="versions-tab-latest-badge"
              className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
            >
              {t("meetings.card.version.latestBadge")}
            </span>
          ) : null}
          {version.is_custom_prompt === true ? (
            <span
              data-testid="versions-tab-custom-prompt-badge"
              className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
            >
              {t("meetingCard.versions.customPrompt")}
            </span>
          ) : null}
        </td>
      </tr>
      <tr>
        <td colSpan={4} className="pb-3">
          <PromptDisclosure
            version={version}
            sameAsVersion={sameAsVersion}
            open={open}
            onToggle={() => setOpen((v) => !v)}
            t={t}
          />
        </td>
      </tr>
    </>
  );
}

interface PromptDisclosureProps {
  version: AnalysisVersionResponse;
  sameAsVersion: number | null;
  open: boolean;
  onToggle: () => void;
  t: TFunction;
}

function PromptDisclosure({
  version,
  sameAsVersion,
  open,
  onToggle,
  t,
}: PromptDisclosureProps) {
  const versionId = version.id;
  const toggleLabel = open
    ? t("meetings.card.version.prompt.toggleCollapse", { n: version.version })
    : t("meetings.card.version.prompt.toggleExpand", { n: version.version });
  return (
    <div className="ml-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="rounded border border-blue-300 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400"
          data-testid={`versions-tab-prompt-toggle-${versionId}`}
          aria-expanded={open}
        >
          <span
            className={`inline-block transition-transform ${
              open ? "rotate-90" : ""
            }`}
            aria-hidden="true"
          >
            ▶
          </span>{" "}
          {toggleLabel.replace(/^[▶▼]\s*/, "")}
        </button>
        {sameAsVersion !== null ? (
          <span
            className="text-xs text-muted-foreground"
            data-testid={`versions-tab-prompt-sameas-${versionId}`}
          >
            {t("meetings.card.version.prompt.sameAs", { n: sameAsVersion })}
          </span>
        ) : null}
      </div>
      {open ? <PromptPanel version={version} t={t} /> : null}
    </div>
  );
}

interface PromptPanelProps {
  version: AnalysisVersionResponse;
  t: TFunction;
}

function PromptPanel({ version, t }: PromptPanelProps) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">(
    "idle",
  );

  useEffect(() => {
    if (copyState === "idle") return;
    const handle = setTimeout(() => setCopyState("idle"), 1500);
    return () => clearTimeout(handle);
  }, [copyState]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(version.prompt_text);
    setCopyState(ok ? "copied" : "error");
  };

  const activeClass = version.prompt_is_active
    ? "bg-emerald-100 text-emerald-800"
    : "bg-slate-100 text-slate-700";

  return (
    <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-3">
      <div
        className="flex items-start justify-between gap-2 text-xs text-slate-700"
        data-testid={`versions-tab-prompt-header-${version.id}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">
            {t("meetings.card.version.prompt.header", {
              n: version.prompt_version_number,
              type: version.prompt_meeting_type_name,
              date: formatPromptDate(version.prompt_created_at),
            })}
          </span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${activeClass}`}
          >
            {version.prompt_is_active
              ? t("meetings.card.version.prompt.active")
              : t("meetings.card.version.prompt.stale")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {copyState !== "idle" ? (
            <span
              className={
                copyState === "copied"
                  ? "text-xs text-emerald-700"
                  : "text-xs text-destructive"
              }
              role="status"
              data-testid={`versions-tab-prompt-copy-status-${version.id}`}
            >
              {copyState === "copied"
                ? t("meetings.card.version.prompt.copied")
                : t("meetings.card.version.prompt.copyFailed")}
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleCopy}
            className="rounded border border-blue-300 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-50"
            data-testid={`versions-tab-prompt-copy-${version.id}`}
          >
            {t("meetings.card.version.prompt.copy")}
          </button>
        </div>
      </div>
      <pre
        data-testid={`versions-tab-prompt-text-${version.id}`}
        className="mt-2 max-h-[280px] overflow-auto whitespace-pre-wrap break-words rounded border border-slate-200 bg-white p-2 font-mono text-xs text-slate-800"
      >
        {version.prompt_text}
      </pre>
    </div>
  );
}

/**
 * Compact local date for the prompt header — `dd.MM.YYYY HH:mm`. Mirrors
 * `versionDateLabel` but keeps the year because the prompt may be older than
 * the meeting itself (admin curated it months ago).
 */
function formatPromptDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}

/**
 * Best-effort clipboard write. Mirrors the helper in `TranscriptTab`:
 * `navigator.clipboard` first (secure-context only), then `execCommand`
 * fallback for plain-http deployments where the modern API throws.
 */
async function copyToClipboard(value: string): Promise<boolean> {
  try {
    if (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      navigator?.clipboard?.writeText !== undefined
    ) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to execCommand fallback
  }
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
